import { LEARNING_PROGRESS_EVENT, learningProgressScope, readLearningProgress, type LearningProgress } from "./learningProgress.ts";

/**
 * 把本地学习进度写透到服务端，给「进度排名」用。续播仍只读本地（learningProgress.ts），这里不回读。
 *
 * - 登录后先把本地已有的记录整份补一次：功能上线前看过的课也算进排名
 * - 之后跟着播放器的进度事件走：看完一节立刻报，其余攒 10 秒一起报
 * - 报失败不丢，等下一次进度变化或下一次登录再报；匿名（票里没有用户名）不报，服务端也认不出是谁
 */
const DEBOUNCE_MS = 10_000;

export interface SyncedLesson { lesson_id: string; position: number; duration: number; completed: boolean; updated_at: number }

/** 本地记录里比上次报过的更新的那些；flush 成功后才算报过 */
export function pendingLessons(progress: LearningProgress, sent: ReadonlyMap<string, number>): SyncedLesson[] {
  return Object.entries(progress)
    .filter(([id, p]) => (sent.get(id) ?? -1) < p.updatedAt)
    .map(([id, p]) => ({ lesson_id: id, position: p.position, duration: p.duration, completed: p.completed, updated_at: p.updatedAt }));
}

/** 返回停止函数。push 由调用方注入（api.pushLearningProgress），便于单测 */
export function startLearningSync(operator: string | null, push: (lessons: SyncedLesson[]) => Promise<unknown>): () => void {
  const scope = learningProgressScope(operator);
  if (scope === "anonymous") return () => {};
  const sent = new Map<string, number>();
  const completedSent = new Set<string>();
  let timer: ReturnType<typeof setTimeout> | null = null;
  let inflight = false;
  let again = false;
  let stopped = false;

  const flush = async () => {
    if (timer) { clearTimeout(timer); timer = null; }
    if (inflight) { again = true; return; }
    const batch = pendingLessons(readLearningProgress(scope).progress, sent);
    if (!batch.length || stopped) return;
    inflight = true;
    try {
      await push(batch);
      for (const l of batch) { sent.set(l.lesson_id, l.updated_at); if (l.completed) completedSent.add(l.lesson_id); }
    } catch { /* 下一次进度变化再报 */ } finally {
      inflight = false;
      if (again && !stopped) { again = false; void flush(); }
    }
  };
  const onChange = (event: Event) => {
    if ((event as CustomEvent<string>).detail !== scope) return;
    const progress = readLearningProgress(scope).progress;
    const newlyDone = Object.entries(progress).some(([id, p]) => p.completed && !completedSent.has(id));
    if (newlyDone) { void flush(); return; }
    if (!timer) timer = setTimeout(() => { void flush(); }, DEBOUNCE_MS);
  };
  const onHide = () => { if (document.visibilityState === "hidden") void flush(); };

  window.addEventListener(LEARNING_PROGRESS_EVENT, onChange);
  document.addEventListener("visibilitychange", onHide);
  void flush();
  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
    window.removeEventListener(LEARNING_PROGRESS_EVENT, onChange);
    document.removeEventListener("visibilitychange", onHide);
  };
}
